import React, { useState, useEffect } from 'react';
import ContentHeader from '../components/ContentHeader';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { tenantBillingAPI } from '../services/api';
import './BillableSeatsPage.css';

function formatRowLabel(row) {
  const staff = [row.staff_first_name, row.staff_last_name].filter(Boolean).join(' ').trim();
  if (staff) return staff;
  return row.email || row.username || `User #${row.user_id}`;
}

function formatSecondary(row) {
  const staff = [row.staff_first_name, row.staff_last_name].filter(Boolean).join(' ').trim();
  const primary = row.email || row.username || '';
  if (staff && primary && primary !== staff) return primary;
  return null;
}

/**
 * Read-only subscription seat usage for tenant admins (Settings → Billable seats).
 */
export default function BillableSeatsPage() {
  const { tenantPath } = useTenantFromUrl();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [count, setCount] = useState(0);
  const [users, setUsers] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    tenantBillingAPI
      .getSummary()
      .then((res) => {
        if (cancelled) return;
        const d = res.data?.data || {};
        setCount(typeof d.billable_user_count === 'number' ? d.billable_user_count : 0);
        setUsers(Array.isArray(d.billable_users) ? d.billable_users : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Failed to load');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="billable-seats-page">
      <ContentHeader
        title="Billable seats"
        backTo={tenantPath('/settings/tenant-settings')}
        backLabel="Back to Tenant Settings"
        titleHint={
          <>
            Users listed here are included in the current <strong>billable seat</strong> count for your organization
            (enabled logins with access to this tenant, counted for subscription). Which accounts are billable is managed
            by Child Family Connect; contact us if something looks wrong.
          </>
        }
      />

      {loading && <p className="billable-seats-muted">Loading…</p>}
      {error && <p className="billable-seats-error">{error}</p>}

      {!loading && !error && (
        <>
          <p className="billable-seats-count-line">
            <span className="billable-seats-count-number">{count}</span>
            <span className="billable-seats-count-label">
              {count === 1 ? 'billable seat' : 'billable seats'}
            </span>
          </p>

          {users.length === 0 ? (
            <p className="billable-seats-muted">No billable users match the current rules.</p>
          ) : (
            <ul className="billable-seats-list" aria-label="Billable users">
              {users.map((row) => {
                const secondary = formatSecondary(row);
                return (
                  <li key={row.user_id}>
                    <span className="billable-seats-list-primary">{formatRowLabel(row)}</span>
                    {secondary && (
                      <span className="billable-seats-list-secondary">{secondary}</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
